import { Site, SiteContextService } from './../../core/site-context.service';
import { LoggerFactory, Logger } from './../../core/logger.service';
import { Component, ElementRef, OnDestroy, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SiteItem } from './site-item';
import { SiteSpecific, UserSession } from 'app/core/site-specific.base';
import { IdentityService } from 'app/core/identity/identity.service';
import { AccountService } from 'app/account/account.service';
import { Account, AccountTypeCustomer, BasicAccount } from 'app/back-office/account.model';
import { CustomersService } from 'app/customers/customers.service';
import { CurrentContextService } from 'app/core/current-context.service';
import { Subscription } from 'rxjs';
import { ConfigureMarketAccountModalComponent } from 'app/shared/configure-market-account-modal/configure-market-account-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { CustomerSelectorService } from '../customer-selector/customer-selector.service';
import { CustomerItem } from '../customer-selector/customer-item';

const NO_SITE_SELECTED = new SiteItem(-1, 'Select Account');

@Component({
  selector: 'app-site-selector',
  templateUrl: './site-selector.component.html',
  styleUrls: ['site-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SiteSelectorComponent extends SiteSpecific implements OnDestroy {
  private readonly logger: Logger;
  private shouldQueryForMoreSites: boolean;
  private allUserAccounts: BasicAccount[] = [];

  public loading: boolean;
  public selectedSite: SiteItem;
  public selectableSites: SiteItem[];
  public filteredSites: SiteItem[];
  public currentContextSubscription: Subscription;
  public onFocusName = true;
  private clickListener: () => void;
  private customerContextSubscription: Subscription;
  public currentAccount: Account;
  public showAddButton: boolean;
  public isDomainConfirmed: boolean;
  public isAccountsLimitReached: boolean;

  private readonly upgradeRoute = '/settings/plan-billing';
  private readonly accountSettingsRoute = '/settings/general';

  @ViewChild('siteInput') public siteInputRef: ElementRef;
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;

  constructor(
    loggerFactory: LoggerFactory,
    userSession: UserSession,
    public readonly siteContext: SiteContextService,
    private readonly identityService: IdentityService,
    private readonly accountService: AccountService,
    private readonly customersService: CustomersService,
    protected currentContextService: CurrentContextService,
    private renderer: Renderer2,
    private readonly matDialog: MatDialog,
    private readonly router: Router,
    private readonly customerSelectorService: CustomerSelectorService,
  ) {
    super(userSession, loggerFactory);
    this.logger = loggerFactory.get('SiteSelectorComponent');
    this.currentContextSubscription = this.currentContextService.currentContextBS.subscribe((currentContext) => {
      if (currentContext) {
        this.currentAccount = currentContext.account;
        this.selectedSite = { ...this.selectedSite, name: currentContext.account.name };
        this.isDomainConfirmed = this.currentAccount.urls?.length > 0;
        this.isAccountsLimitReached = currentContext.customer.siteIds?.length >= currentContext.limitations.accounts;
        const isSelfService = currentContext.customer.type === AccountTypeCustomer.SelfService;
        this.showAddButton = isSelfService && !this.currentAccount.isExpired;
      }
    });
    this.customerContextSubscription = this.customerSelectorService.selectedCustomer$.subscribe((customer) => {
      this.onCustomerChanged(customer);
    });
    this.clickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.site-selector__autocomplete') && !target.closest('.site-selector__input-container')) {
        this.onFocusName = false;
      }
    });
  }
  protected async onSiteChanged(site: Site) {
    await this.initializeSiteList(site);
  }

  public onFocus(event: any): void {
    this.onFocusName = true;
    this.queryForMoreSites(event);
  }

  public onKeyUp(event: any): void {
    this.setFilteredSites(event.target.value);
  }

  public async onOptionClick(siteId: number): Promise<void> {
    this.onFocusName = true;
    this.logger.debug(`${siteId} is clicked`);
    await this.setSelectedSiteAsync(siteId);
  }

  public addAccount() {
    this.resetSiteSelector();

    if (!this.isDomainConfirmed) {
      this.router.navigate([this.accountSettingsRoute], { queryParams: { validate: 'domain' } });
      return;
    }
    if (this.isAccountsLimitReached) {
      this.router.navigate([this.upgradeRoute], { queryParams: { 'open-flyout': 'true' } });
      return;
    }

    this.matDialog.open(ConfigureMarketAccountModalComponent, {
      width: '500px',
      data: { account: this.currentAccount, createNewAccount: true },
    });
  }

  private resetSiteSelector() {
    this.onFocusName = false; // Show current account name again
    this.siteInputRef.nativeElement.blur(); // Remove focus
    this.autocomplete.closePanel(); // Close the autocomplete
  }

  private initializeSiteList(site: Site): Promise<void> {
    // Set initial and selected state
    if (this.siteContext.isBusy) {
      this.onFocusName = true;
    } else {
      this.onFocusName = false;
    }
    if (site) {
      this.selectableSites = [site];
      this.selectedSite = site;
    }

    const siteIds = this.identityService.currentIdentity.getAllSiteIds();
    if (siteIds.length <= 1) {
      // We have 1 or less accounts, we don't need to query for more accounts
      return;
    }

    // We should load more accounts when we need them later
    this.shouldQueryForMoreSites = true;
  }

  private onCustomerChanged(customer: CustomerItem | null): void {
    if (!customer) {
      this.loadUserOwnSites();
    } else {
      this.loadCustomerSpecificSites(customer.siteIds);
    }
  }

  private loadAllUserAccounts(onLoaded: (accounts: BasicAccount[]) => void): void {
    this.loading = true;

    this.accountService.getAllAccounts().subscribe({
      next: (allAccounts: BasicAccount[]) => {
        this.loading = false;
        if (!allAccounts || allAccounts.length === 0) {
          this.logger.info('No site has been found', 'all accounts:', allAccounts);
          this.selectableSites = [];
          this.selectedSite = undefined;
          return;
        }

        this.selectableSites = allAccounts.map((s) => new SiteItem(s.siteId, s.name));
        onLoaded(allAccounts);
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private loadUserOwnSites(): void {
    this.loadAllUserAccounts((allAccounts: BasicAccount[]) => {
      this.allUserAccounts = allAccounts;
      this.setFilteredSites('');

      const isCurrentSelectionValid =
        this.selectedSite && this.selectableSites.find((s) => s.id === this.selectedSite.id);

      if (!isCurrentSelectionValid && this.selectableSites.length > 0) {
        const currentCustomer = this.customerSelectorService.getCurrentCustomer();
        if (currentCustomer) {
          this.selectedSite = NO_SITE_SELECTED;
          this.onFocusName = false;
        } else {
          this.setSelectedSiteAsync(this.selectableSites[0].id);
        }
      }
    });
  }

  private loadCustomerSpecificSites(siteIds: number[]): void {
    if (!siteIds || siteIds.length === 0) {
      this.selectableSites = [];
      this.filteredSites = [];
      this.selectedSite = undefined;
      return;
    }
    const filteredAccounts = this.allUserAccounts.filter((account) => siteIds.includes(account.siteId));

    if (filteredAccounts.length === 0) {
      this.logger.info('No accounts found for customer', 'siteIds:', siteIds);
      this.selectableSites = [];
      this.filteredSites = [];
      this.selectedSite = NO_SITE_SELECTED;
      return;
    }

    this.selectableSites = filteredAccounts.map((s) => new SiteItem(s.siteId, s.name));
    this.setFilteredSites('');

    const isCurrentSelectionValid =
      this.selectedSite &&
      this.selectedSite.id !== NO_SITE_SELECTED.id &&
      this.selectableSites.find((s) => s.id === this.selectedSite.id);

    if (!isCurrentSelectionValid) {
      this.selectedSite = NO_SITE_SELECTED;
      this.onFocusName = false;
    }
  }

  private async queryForMoreSites(event: any): Promise<void> {
    if (this.shouldQueryForMoreSites) {
      this.shouldQueryForMoreSites = false;
      const currentCustomer = this.customerSelectorService.getCurrentCustomer();

      if (currentCustomer) {
        this.loadCustomerSpecificSites(currentCustomer.siteIds);
        this.initTextBox(event);
      } else {
        this.loadAllUserAccounts(() => {
          this.initTextBox(event);
        });
      }
    } else {
      this.initTextBox(event);
    }
  }

  private initTextBox(event: any) {
    event.target.value = '';
    this.setFilteredSites(event.target.value);
  }

  private setFilteredSites(currentInput: string): void {
    if (this.selectableSites != null) {
      this.filteredSites = this.selectableSites
        .filter((site) => new RegExp(currentInput, 'i').test(site.name))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  private async setSelectedSiteAsync(siteId: number): Promise<void> {
    this.logger.debug(`Attempting to change current account to ${siteId}`);
    if (!this.selectableSites) {
      return;
    }
    const site = this.selectableSites.find((s) => s.id === siteId);
    if (!site) {
      this.logger.error('Account is not selectable');
      return;
    }
    this.customersService.clearCurrentCustomerCache(); // Clear cache before setting new site as current so new customer is loaded
    await this.siteContext.setCurrentSiteAsync(site.id);
  }

  public ngOnDestroy(): void {
    if (this.currentContextSubscription) {
      this.currentContextSubscription.unsubscribe();
    }
    if (this.clickListener) {
      this.clickListener();
    }
    if (this.customerContextSubscription) {
      this.customerContextSubscription.unsubscribe();
    }
    super.ngOnDestroy();
  }
}
